import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '@shared/i18n';
import { useOrderDetail } from '../hooks/useOrderDetail';
import { OrderStatusBadge } from '../components/OrderStatusBadge';

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency }).format(value);
}

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: order, isLoading } = useOrderDetail(orderId);

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">{t('orders.loading')}</div>;
  }

  if (!order) {
    return <div className="text-center py-8 text-gray-500">{t('orders.not_found')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/orders')} className="btn btn-outline text-sm">
          {t('products.back')}
        </button>
        <h2 className="text-xl font-semibold text-gray-900">
          {t('orders.order')} #{order.number}
        </h2>
        <OrderStatusBadge status={order.status} statusName={order.statusName} />
      </div>

      <div className="card p-6">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">{t('orders.date')}:</span>{' '}
            <span className="font-medium text-gray-900">
              {new Date(order.createdAt).toLocaleString('ru-RU')}
            </span>
          </div>
          <div>
            <span className="text-gray-500">{t('orders.customer')}:</span>{' '}
            <span className="font-medium text-gray-900">{order.customerName ?? '—'}</span>
          </div>
          <div>
            <span className="text-gray-500">{t('orders.total')}:</span>{' '}
            <span className="font-medium text-gray-900">
              {formatCurrency(order.total, order.currency)}
            </span>
          </div>
          {order.paymentStatus && (
            <div>
              <span className="text-gray-500">{t('orders.payment')}:</span>{' '}
              <span className="font-medium text-gray-900">{order.paymentStatus}</span>
            </div>
          )}
          {order.shipmentStatus && (
            <div>
              <span className="text-gray-500">{t('orders.shipment')}:</span>{' '}
              <span className="font-medium text-gray-900">{order.shipmentStatus}</span>
            </div>
          )}
        </div>
      </div>

      {order.items.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('orders.items')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="pb-3 font-medium">{t('orders.item_name')}</th>
                  <th className="pb-3 font-medium text-right">{t('orders.quantity')}</th>
                  <th className="pb-3 font-medium text-right">{t('orders.price')}</th>
                  <th className="pb-3 font-medium text-right">{t('orders.item_total')}</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item) => (
                  <tr key={item.productId} className="border-b border-gray-100">
                    <td className="py-3 text-gray-900">{item.name}</td>
                    <td className="py-3 text-right text-gray-600">{item.quantity}</td>
                    <td className="py-3 text-right text-gray-600">
                      {formatCurrency(item.price, order.currency)}
                    </td>
                    <td className="py-3 text-right font-medium text-gray-900">
                      {formatCurrency(item.price * item.quantity, order.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
